import {
  Box,
  Button,
  Dialog,
  IconButton,
  Typography,
  ToggleButtonGroup,
} from '@mui/material';
import { styled, alpha } from '@mui/material/styles';
import {
  type AssistantModalTheme,
  modalThemeTokens,
  buildModalCssVars,
} from '../AssistantEditorModals/theme';

const StyledDialog = styled(Dialog)({
  '& .MuiBackdrop-root': { backgroundColor: 'var(--am-backdrop)' },
  '& .MuiDialog-paper': {
    borderRadius: '16px',
    padding: 0,
    boxShadow: '0px 4px 24px rgba(0, 0, 0, 0.12)',
    background: 'var(--am-paper-bg)',
  },
});

const ModalContent = styled(Box)({
  background: 'var(--am-paper-bg)',
  borderRadius: '16px',
  width: '100%',
  position: 'relative',
});

const ModalHeader = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  padding: theme.spacing(3),
}));

const ModalBody = styled(Box)(({ theme }) => ({
  padding: theme.spacing(3),
  display: 'flex',
  flexDirection: 'column',
  gap: theme.spacing(2),
}));

const ModalActions = styled(Box)(({ theme }) => ({
  display: 'flex',
  gap: theme.spacing(1.5),
  justifyContent: 'flex-end',
  marginTop: theme.spacing(1),
}));

export const CloseButton = styled(IconButton)({
  width: '32px',
  height: '32px',
  color: 'var(--am-text-secondary)',
  padding: '4px',
  '&:hover': {
    backgroundColor: 'var(--am-surface-2)',
    color: 'var(--am-text-primary)',
  },
});

export const ModalTitle = styled(Typography)({
  fontSize: '20px',
  fontWeight: 600,
}) as typeof Typography;

export const PrimaryButton = styled(Button)(({ theme }) => ({
  height: '48px',
  borderRadius: '8px',
  fontSize: '16px',
  fontWeight: 500,
  lineHeight: '24px',
  textTransform: 'none',
  color: theme.palette.common.white,
  backgroundColor: theme.palette.primary.main,
  padding: '12px 24px',
  minWidth: '140px',
  border: 'none',
  '&:hover': { backgroundColor: theme.palette.primary.dark },
  '&:disabled': {
    backgroundColor: 'var(--am-border-mid)',
    color: 'var(--am-text-secondary)',
  },
}));

export const CancelButton = styled(Button)({
  height: '48px',
  borderRadius: '8px',
  fontSize: '16px',
  fontWeight: 500,
  lineHeight: '24px',
  textTransform: 'none',
  color: 'var(--am-text-primary)',
  backgroundColor: 'transparent',
  border: '2px solid var(--am-cancel-border)',
  padding: '12px 24px',
  minWidth: '140px',
  '&:hover': {
    backgroundColor: 'var(--am-cancel-hover-bg)',
    border: '2px solid var(--am-cancel-hover-border)',
  },
  '&:disabled': { opacity: 0.5 },
});

export const ScriptsDialog = styled(StyledDialog, {
  shouldForwardProp: (prop) => prop !== '$theme',
})<{ $theme?: AssistantModalTheme }>(({ $theme = 'light' }) => {
  const tokens = modalThemeTokens[$theme];
  const cssVars = buildModalCssVars(tokens);
  return {
    // Set CSS variables on the dialog root so they cascade to paper + all children
    ...(cssVars as any),
    '& .MuiBackdrop-root': { backgroundColor: tokens.backdrop },
    '& .MuiDialog-paper': {
      width: '100%',
      maxWidth: 800,
      overflow: 'hidden',
      display: 'flex',
      flexDirection: 'column',
      borderRadius: 16,
      background: tokens.paperBg,
      // MUI form element overrides so Select/inputs respect the theme
      '& .MuiInputBase-root': {
        color: 'var(--am-text-primary)',
        backgroundColor: 'var(--am-input-bg)',
      },
      '& .MuiOutlinedInput-notchedOutline': {
        borderColor: 'var(--am-border-mid)',
      },
      '& .MuiSelect-icon': {
        color: 'var(--am-text-secondary)',
      },
      '& .MuiDivider-root': {
        borderColor: 'var(--am-border)',
      },
    },
    '& .MuiBox-root': { maxWidth: '100%' },
  };
});

export const ScriptsModalContent = styled(ModalContent)({
  display: 'flex',
  flexDirection: 'column',
  flex: 1,
  overflow: 'hidden',
  maxWidth: '100%',
  borderRadius: 16,
  boxShadow: 'none',
});

export const ScriptsModalHeader = styled(ModalHeader)({
  padding: '16px 24px',
  position: 'sticky',
  top: -18,
  zIndex: 1,
  background: 'var(--am-header-bg)',
  flexShrink: 0,
  borderBottom: '1px solid var(--am-border)',
});

export const HeaderTitleRow = styled(Box)({
  display: 'flex',
  alignItems: 'center',
  gap: 8,
});

export const HeaderActionsRow = styled(Box)({
  display: 'flex',
  alignItems: 'center',
  gap: 16,
});

export const UnsavedChangesRow = styled(Box)({
  display: 'flex',
  alignItems: 'center',
  gap: 6,
});

export const UnsavedChangesDot = styled(Box)(({ theme }) => ({
  width: 8,
  height: 8,
  borderRadius: '50%',
  backgroundColor: theme.palette.warning.main,
  flexShrink: 0,
}));

export const UnsavedChangesText = styled(Typography)(({ theme }) => ({
  fontSize: '13px',
  color: theme.palette.warning.main,
  fontWeight: 500,
})) as typeof Typography;

export const ScriptsModalBody = styled(ModalBody)({
  padding: '16px 24px 24px',
  gap: 24,
  overflowY: 'auto',
  flex: 1,
});

export const ScriptsModalActions = styled(ModalActions)({
  justifyContent: 'space-between',
  flexShrink: 0,
  padding: '16px 24px',
  borderTop: '1px solid var(--am-border)',
  background: 'var(--am-header-bg)',
});

export const StepsHeaderRow = styled(Box)({
  display: 'flex',
  alignItems: 'flex-start',
  justifyContent: 'space-between',
  marginBottom: 16,
});

export const StepsTitle = styled(Typography)({
  fontWeight: 600,
  fontSize: '15px',
  color: 'var(--am-text-primary)',
}) as typeof Typography;

export const AddStepButton = styled(Button)({
  fontSize: '13px',
  textTransform: 'none',
  borderRadius: 4,
  padding: '0 12px',
  height: 32,
  minHeight: 'unset',
  whiteSpace: 'nowrap',
});

export const FixedStepsBar = styled(Box)({
  position: 'fixed',
  zIndex: 1400,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  background: 'var(--am-header-bg)',
  borderBottom: '1px solid var(--am-border)',
  padding: '8px 24px',
});

export const StepsContainer = styled(Box)({
  display: 'flex',
  flexDirection: 'column',
  gap: 20,
});

export const ScriptToggleButtonGroup = styled(ToggleButtonGroup)(
  ({ theme }) => ({
    gap: 6,
    '& .MuiToggleButtonGroup-grouped': {
      border: '0 !important',
      borderRadius: '6px !important',
    },
    '& .MuiToggleButton-root': {
      fontSize: '13px',
      textTransform: 'none',
      padding: '6px 16px',
      borderRadius: '6px !important',
      border: '2px solid var(--am-border-mid) !important',
      backgroundColor: 'transparent',
      color: 'var(--am-text-secondary)',
      fontWeight: 400,
      '&.Mui-selected': {
        color: theme.palette.primary.main,
        fontWeight: 600,
        backgroundColor: alpha(theme.palette.primary.main, 0.08),
        border: `2px solid ${theme.palette.primary.main} !important`,
      },
      '&.Mui-selected:hover': {
        backgroundColor: alpha(theme.palette.primary.main, 0.12),
      },
    },
  }),
);

export const SectionLabelText = styled(Typography)({
  fontSize: '11px',
  fontWeight: 600,
  color: 'var(--am-text-secondary)',
  letterSpacing: '0.08em',
  textTransform: 'uppercase',
  marginBottom: 8,
}) as typeof Typography;

export const TriggerSectionTitle = styled(Typography)({
  fontWeight: 700,
  fontSize: '15px',
  marginBottom: 4,
  color: 'var(--am-text-primary)',
}) as typeof Typography;

export const TriggerSectionSubtitle = styled(Typography)({
  fontSize: '14px',
  color: 'var(--am-text-secondary)',
  marginBottom: 12,
}) as typeof Typography;

export const LanguageTab = styled(Box, {
  shouldForwardProp: (prop) => prop !== 'active' && prop !== 'complete',
})<{ active?: boolean; complete?: boolean }>(({ theme, active, complete }) => {
  let borderColor = 'var(--am-border-mid)';
  let backgroundColor = 'transparent';
  let color = 'var(--am-text-primary)';
  let hoverBorderColor = 'var(--am-border-heavy)';
  let fontWeight = 400;

  if (active) {
    borderColor = theme.palette.primary.main;
    backgroundColor = alpha(theme.palette.primary.main, 0.08);
    color = theme.palette.primary.main;
    hoverBorderColor = theme.palette.primary.main;
    fontWeight = 600;
  } else if (complete) {
    borderColor = theme.palette.success.main;
    backgroundColor = alpha(theme.palette.success.main, 0.06);
    color = theme.palette.success.main;
    hoverBorderColor = theme.palette.success.main;
  }

  return {
    display: 'flex',
    alignItems: 'center',
    gap: 6,
    padding: '4px 10px',
    borderRadius: 6,
    border: `1.5px solid ${borderColor}`,
    backgroundColor,
    cursor: 'pointer',
    userSelect: 'none',
    fontSize: '12px',
    fontWeight,
    color,
    transition: 'all 0.15s',
    '&:hover': { borderColor: hoverBorderColor },
  };
});
